import React, { useEffect, useState } from "react";
import { ReactComponent as Haram } from "../../assets/svg/PaymentMethods/Haram.svg";
import { ReactComponent as BankTransfer } from "../../assets/svg/PaymentMethods/BankTransfer.svg";
import { ReactComponent as CashOnSite } from "../../assets/svg/PaymentMethods/CashOnSite.svg";
import { ReactComponent as QR } from "../../assets/svg/PaymentMethods/QR.svg";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import axios from "axios";
import { API } from "../../shared/api/Api";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { showToast } from "../../utils/utils";

const PlacementTestEnrollmentPage = () => {
  const user = useSelector(selectUserInformations);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [test, setTest] = useState(location.state?.test || null);
  const enrollment = test?.enrollment ?? test?.pivot ?? null;
  const isPaymentVerified =
    enrollment?.payment_status === "paid" || enrollment?.payment_status === "verified";
  const hasPendingPaymentRequest =
    enrollment?.result_status === "pending_payment" || enrollment?.payment_status === "pending";
  const currentMethod = enrollment?.payment?.method || null;

  const isPublicPlacementFlow =
    typeof location.pathname === "string" &&
    location.pathname.startsWith("/placement-tests/") &&
    location.pathname.endsWith("/payment");
  const placementPublicDetailPath = `/placement-tests/${id}`;

  useEffect(() => {
    const fetchTest = async () => {
      if (test) return;
      try {
        const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};
        const { data } = await axios.get(`${API.PlacementTests.get}/${id}`, { headers });
        if (data.status === 200) {
          setTest(data.data);
        }
      } catch {
        // Keep payment page usable even if details fetch fails.
      }
    };
    fetchTest();
  }, [id, test, user?.token]);

  useEffect(() => {
    if (!selectedMethod && currentMethod && !isPaymentVerified) {
      setSelectedMethod(currentMethod);
    }
  }, [currentMethod, isPaymentVerified, selectedMethod]);

  const handleEnroll = async () => {
    if (isPaymentVerified) {
      showToast("Payment Verified", "Your payment is already verified and cannot be changed.", "info");
      return;
    }

    if (!user?.student?.id) {
      showToast("Error", "You must be registered as a student.", "error");
      return;
    }
    if (!selectedMethod) {
      showToast("Error", "Please select a payment method.", "error");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API.PlacementTests.startPayment}/${id}/payments/start`,
        { method: selectedMethod },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      if (data.status === 200 && data.errorCode === 0) {
        const successMessage = hasPendingPaymentRequest
          ? "Your payment method was updated. The existing payment request is still pending admin review."
          : "Payment request submitted. An admin will verify it shortly.";
        showToast("Success", successMessage, "success");
        navigate(`/dashboard/placement-tests/${id}`);
      } else {
        showToast("Error", data?.message || "Failed to submit payment request.", "error");
      }
    } catch (error) {
      showToast("Error", error.response?.data?.message || "Failed to process payment.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-screen min-h-screen py-4 sm:py-6 md:py-8 px-2 sm:px-4 md:px-6 lg:px-8 bg-gray-50">
      {/* Header */}
      <div className="flex flex-col md:flex-row px-4 sm:px-6 md:px-10 py-6 sm:py-8 md:py-10 bg-white rounded-2xl border border-gray-200 shadow-sm mb-6">
        <div>
          <button
            onClick={() =>
              navigate(isPublicPlacementFlow ? placementPublicDetailPath : `/dashboard/placement-tests/${id}`)
            }
            className="text-sm text-primary hover:underline mb-2"
          >
            ← {isPublicPlacementFlow ? "Back to placement test" : "Back to my placement test"}
          </button>
          <h2 className="font-anek-odia text-2xl sm:text-3xl md:text-5xl font-medium text-primary leading-relaxed">
            Placement Test Payment
          </h2>
          {test?.title && (
            <p className="text-gray-500 mt-1 text-sm sm:text-base">
              Enrolling in: <span className="font-semibold text-gray-800">{test.title}</span>
            </p>
          )}
          {!!test?.type && (
            <p className="text-xs text-gray-500 mt-2">
              Type:{" "}
              <span className="font-medium text-gray-700">
                {test.type === "pre_enrollment" ? "Course placement" : "Standalone"}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* Payment Methods */}
      {isPaymentVerified ? (
        <div className="max-w-3xl mx-auto mb-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 sm:p-5 text-emerald-800">
          <p className="font-semibold text-sm sm:text-base">Payment already verified</p>
          <p className="mt-1 text-xs sm:text-sm">
            Your payment has already been approved by admin, so payment method changes are locked.
          </p>
          <button
            onClick={() => navigate(`/dashboard/placement-tests/${id}`)}
            className="mt-3 inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition"
          >
            Go to my placement test
          </button>
        </div>
      ) : hasPendingPaymentRequest ? (
        <div className="max-w-3xl mx-auto mb-2 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-5 text-amber-800">
          <p className="font-semibold text-sm sm:text-base">You already have a pending payment request</p>
          <p className="mt-1 text-xs sm:text-sm leading-relaxed">
            Choosing a different method here will update your existing pending request (it will not create a duplicate
            payment). Use this only if you want to switch your payment method before admin verification.
          </p>
          {currentMethod && (
            <p className="mt-2 text-xs sm:text-sm">
              Current method:{" "}
              <span className="font-semibold text-amber-900">{String(currentMethod).replaceAll("_", " ")}</span>
            </p>
          )}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 py-8">
        {/* Haram Transfer */}
        <div
          className={`bg-white h-[35rem] rounded-2xl border-2 ${
            selectedMethod === "haram_transfer" ? "border-blue-500" : "border-gray-200"
          } shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md transform hover:-translate-y-1 ${
            isPaymentVerified ? "cursor-not-allowed opacity-60" : "cursor-pointer"
          }`}
          onClick={() => !isPaymentVerified && setSelectedMethod("haram_transfer")}
        >
          <div className="p-3 sm:p-4 flex flex-col items-center">
            <div className="p-2 sm:p-3 rounded-md mb-2 sm:mb-4 w-full flex items-center justify-center">
              <Haram className="w-full max-w-[200px] h-auto" />
            </div>
            <div className="w-full h-48 sm:h-56 md:h-64 bg-white flex items-center justify-center">
              <div className="w-40 sm:w-48 md:w-56 h-40 sm:h-48 md:h-56 rounded-lg overflow-hidden">
                <QR className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>

        {/* Bank Transfer */}
        <div
          className={`bg-white h-[35rem] rounded-2xl border-2 ${
            selectedMethod === "bank_transfer" ? "border-blue-500" : "border-gray-200"
          } shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md transform hover:-translate-y-1 ${
            isPaymentVerified ? "cursor-not-allowed opacity-60" : "cursor-pointer"
          }`}
          onClick={() => !isPaymentVerified && setSelectedMethod("bank_transfer")}
        >
          <div className="p-3 sm:p-4 flex flex-col items-center">
            <div className="p-2 sm:p-3 rounded-md mb-2 sm:mb-4 w-full flex items-center justify-center">
              <BankTransfer className="w-full max-w-[200px] h-auto" />
            </div>
            <div className="w-full h-48 sm:h-56 md:h-64 bg-white flex items-center justify-center">
              <div className="w-40 sm:w-48 md:w-56 h-40 sm:h-48 md:h-56 rounded-lg overflow-hidden">
                <QR className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>

        {/* Cash On Site */}
        <div
          className={`bg-white h-auto min-h-[35rem] rounded-2xl border-2 ${
            selectedMethod === "cash_on_site" ? "border-blue-500" : "border-gray-200"
          } shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md transform hover:-translate-y-1 ${
            isPaymentVerified ? "cursor-not-allowed opacity-60" : "cursor-pointer"
          }`}
          onClick={() => !isPaymentVerified && setSelectedMethod("cash_on_site")}
        >
          <div className="p-3 sm:p-4 flex flex-col items-center h-full">
            <div className="p-2 sm:p-3 rounded-md mb-4 sm:mb-6 w-full flex items-center justify-center">
              <CashOnSite className="w-full max-w-[180px] h-auto" />
            </div>
            <div className="w-full flex-grow flex flex-col items-center justify-start space-y-4 pt-2 sm:pt-4 px-3 sm:px-5">
              <div className="w-full flex flex-row mb-2 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="w-14 sm:w-16 h-auto min-h-[4rem] bg-primary flex items-center justify-center text-white text-xl sm:text-2xl font-bold">
                  1
                </div>
                <div className="flex-1 border border-gray-300 p-3 sm:p-4 flex flex-col justify-center min-h-[4rem]">
                  <p className="font-medium text-sm sm:text-base text-gray-800">DAMASCUS</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">BARAMKEH - AL-MAAZ STREET</p>
                </div>
              </div>
              <div className="w-full flex flex-row rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="w-14 sm:w-16 h-auto min-h-[4rem] bg-primary flex items-center justify-center text-white text-xl sm:text-2xl font-bold">
                  2
                </div>
                <div className="flex-1 border border-gray-300 p-3 sm:p-4 flex flex-col justify-center min-h-[4rem]">
                  <p className="font-medium text-sm sm:text-base text-gray-800">DAMASCUS</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">JARAMANAH</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm button */}
      {selectedMethod && !isPaymentVerified && (
        <div className="mt-8 flex justify-center">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-3">
              Selected method: <span className="font-semibold text-gray-800">{selectedMethod.replaceAll("_", " ")}</span>
            </p>
            <button
              className="bg-primary text-white font-normal py-3 px-8 rounded-lg text-xl shadow-md hover:bg-blue-700 transition-colors duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleEnroll}
              disabled={loading}
            >
              {loading ? "Processing..." : "Confirm Payment"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlacementTestEnrollmentPage;
